import { useMutation } from "@tanstack/react-query";
import { PlaidConnection } from "@common/types/plaidConnection";
import { CreateBankAccountConnectionDto } from "@common/dto/onboarding/create-bank-account-connection.dto";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { UserIntegrationsResponse } from "@common/dto/user/user-integrations-response.dto";
import { ConnectionProviderType } from "@common/types/financialConnection";
import {
    connectNewPlaidAccounts,
    connectPlaidConnection,
    connectWithPlaid,
    deletePlaidConnection,
} from "@lib/plaidConnection";
import { usePlaidFinancialAccounts } from "@hooks/useFinancialAccounts";
import {
    addFinancialConnectionInQueryData,
    clearConnectionErrorInQueryData,
    entitiesAccountsQueryKey,
} from "@queries/entitiesAccounts";
import { useConnectAccount } from "@components/connectAccount/ConnectAccount.context";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { invalidateAccountRelatedQueries } from "@api/account.api";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { isViewOnlyRefusalError } from "@lib/viewOnlyRefusal";
import { useScope } from "@state/scopeContext.lib";
import {
    financialConnectionsQueryKey,
    plaidConnectionsQueryKey,
    queryClient,
} from "../queryClient";

export function useBankConnectionCreationMutation(options?: {
    skipAccountTypeConfirmation?: boolean;
}) {
    const skipAccountTypeConfirmation =
        options?.skipAccountTypeConfirmation ?? false;
    const { updateConnectAccountState } = useConnectAccount();
    const { workspaceId, refreshConnections } = useConnectTarget();

    return useMutation({
        mutationFn: (data: CreateBankAccountConnectionDto) =>
            connectPlaidConnection(data),
        onSuccess: async ({ connection, connectionSaved, foreign }) => {
            if (connectionSaved) {
                queryClient.setQueryData<PlaidConnection[]>(
                    plaidConnectionsQueryKey,
                    (connections) => [...(connections ?? []), connection],
                );
                addFinancialConnectionInQueryData(queryClient, connection);
                invalidateAccountRelatedQueries({ workspaceId });
                await refreshConnections({
                    provider: "plaid",
                    connectionId: String(connection.id),
                });
            }

            if (skipAccountTypeConfirmation) {
                if (!connectionSaved || foreign.length > 0) {
                    updateConnectAccountState({
                        isConnecting: false,
                        connectionSaved,
                        connection,
                        foreign,
                        skipAccountTypeConfirmation: true,
                    });
                } else {
                    updateConnectAccountState({
                        isConnecting: false,
                    });
                }
                return;
            }

            const hasAccountsNotReadyForSync = connection.accounts.some(
                (a) => !a.financialAccount?.entityId,
            );
            if (hasAccountsNotReadyForSync || foreign.length > 0) {
                updateConnectAccountState({
                    isConnecting: false,
                    connectionSaved,
                    connection,
                    foreign,
                });
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
            }
        },
    });
}

export function useBankConnectionRemovalMutation(connection: PlaidConnection) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: () => deletePlaidConnection(connection.id),
        onSuccess: () => {
            queryClient.setQueryData<PlaidConnection[] | undefined>(
                plaidConnectionsQueryKey,
                (connections) =>
                    connections?.filter((c) => c.id !== connection.id) ?? [],
            );
            queryClient.setQueriesData<UserIntegrationsResponse | undefined>(
                { queryKey: [financialConnectionsQueryKey] },
                (response) =>
                    response
                        ? {
                              financialConnections:
                                  response.financialConnections.filter(
                                      (fc) =>
                                          fc.connection.id !== connection.id ||
                                          fc.connection.connectionProvider !==
                                              ConnectionProviderType.PLAID,
                                  ),
                          }
                        : undefined,
            );
            queryClient.invalidateQueries({
                queryKey: [entitiesAccountsQueryKey],
            });
            invalidateAccountRelatedQueries({ workspaceId });
        },
    });
}

export function usePlaidConnectionFixingMutation(
    connection: PlaidConnection,
    onSuccess?: () => unknown,
) {
    const { workspaceId, organizationId } = useConnectTarget();

    return useMutation({
        mutationFn: () =>
            connectWithPlaid({
                connectionId: connection.id,
                workspaceId,
                organizationId,
            }),
        onSuccess: async ({ connected }) => {
            if (connected) {
                clearConnectionErrorInQueryData(queryClient, connection.id);
                onSuccess?.();

                queryClient.setQueryData<PlaidConnection[] | undefined>(
                    plaidConnectionsQueryKey,
                    (connections) =>
                        connections?.map((c) =>
                            c.id === connection.id
                                ? {
                                      ...c,
                                      error: null,
                                  }
                                : c,
                        ) ?? [],
                );

                await queryClient.invalidateQueries({
                    queryKey: [financialConnectionsQueryKey],
                });
            }
        },
    });
}

export function useRefreshPlaidLinkMutation(
    connection: Pick<PlaidConnection, "id">,
) {
    const { toast, error: toastError } = useToaster();
    const { workspaceId, organizationId } = useConnectTarget();

    return useMutation({
        mutationFn: () =>
            connectWithPlaid({
                connectionId: connection.id,
                workspaceId,
                organizationId,
            }),
        onSuccess: async (result) => {
            if (!result.connected) {
                return;
            }

            toast("Connection refreshed");
            await invalidatePlaidConnectionQueries();
        },
        onError: (error) => {
            if (isViewOnlyRefusalError(error)) {
                return;
            }
            toastError(
                "Plaid error encountered. Fix the connection to continue syncing.",
            );
        },
    });
}

async function invalidatePlaidConnectionQueries(): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: [financialConnectionsQueryKey],
    });
    await queryClient.invalidateQueries({
        queryKey: [entitiesAccountsQueryKey],
    });
}

export function useEnablePlaidStatementsMutation(
    connection: Pick<PlaidConnection, "id">,
) {
    const { toast, error: toastError } = useToaster();
    const { workspaceId, organizationId } = useConnectTarget();

    return useMutation({
        mutationFn: () =>
            connectWithPlaid({
                connectionId: connection.id,
                enableStatements: true,
                requireRelinkSuccess: true,
                workspaceId,
                organizationId,
            }),
        onSuccess: async (result) => {
            if (!result.connected) {
                return;
            }

            if (!result.relinkResult?.statementsApiEnabled) {
                toastError(
                    "Plaid did not enable statement sync for this connection. Try again or contact support.",
                );
                void invalidatePlaidConnectionQueries();
                return;
            }

            toast("Statement sync enabled");
            void invalidatePlaidConnectionQueries();
        },
        onError: () => {
            void invalidatePlaidConnectionQueries();
            toastError(
                "Couldn't enable statement sync in Plaid. Try again or contact support.",
            );
        },
    });
}

export function useAddPlaidAccountsMutation(onSuccess?: () => void) {
    const { toast } = useToaster();
    const allAccounts = usePlaidFinancialAccounts();
    const { updateConnectAccountState } = useConnectAccount();
    const { workspaceId, organizationId } = useConnectTarget();

    return useMutation({
        mutationFn: async (connection: PlaidConnection) => {
            const { connected } = await connectWithPlaid({
                connectionId: connection.id,
                addAccounts: true,
                workspaceId,
                organizationId,
            });

            if (connected) {
                updateConnectAccountState({
                    isConnecting: true,
                });

                return await connectNewPlaidAccounts(connection);
            }
        },

        onSuccess: async (response, connection) => {
            const connectionAccounts = allAccounts
                .flatMap((a) => a.integrationAccounts ?? [])
                .filter((ia) => ia.connectionId === connection.id);
            const currentAccountsIds = connectionAccounts.map((a) => a.id);

            if (response) {
                const { connection: updatedConnection, foreign } = response;
                const newAccounts: IntegrationAccount[] = [];

                updatedConnection.accounts.forEach((a) => {
                    const account = { ...a, connection };
                    if (!currentAccountsIds.includes(a.id)) {
                        newAccounts.push(account);
                    }
                });

                queryClient.invalidateQueries({
                    queryKey: [entitiesAccountsQueryKey],
                });
                invalidateAccountRelatedQueries({ workspaceId });

                if (
                    newAccounts.some((a) => !a.financialAccount?.entityId) ||
                    foreign.length > 0
                ) {
                    updateConnectAccountState({
                        isConnecting: false,
                        connection: updatedConnection,
                        connectionSaved: true,
                        foreign,
                    });
                } else {
                    updateConnectAccountState({
                        isConnecting: false,
                    });
                    toast("Accounts reconnected");
                }

                queryClient.setQueryData<PlaidConnection[] | undefined>(
                    plaidConnectionsQueryKey,
                    (connections) =>
                        connections?.map((c) =>
                            c.id === updatedConnection.id
                                ? updatedConnection
                                : c,
                        ) ?? [],
                );

                await queryClient.invalidateQueries({
                    queryKey: [financialConnectionsQueryKey],
                });
                onSuccess?.();
            } else {
                updateConnectAccountState({
                    isConnecting: false,
                });
            }
        },
        onError: () => {
            updateConnectAccountState({
                isConnecting: false,
            });
        },
    });
}
